/**
 * ConnectionStatusBanner — Minimal connection status indicator.
 *
 * Modern pill design: subtle, unobtrusive, auto-dismissing.
 * "Syncing" when reconnecting, "Live" when restored.
 */

import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useTranslation } from 'react-i18next';

interface ConnectionStatusBannerProps {
  isConnected: boolean;
  isReconnecting?: boolean;
}

type BannerState = 'hidden' | 'connecting' | 'restored';

export function ConnectionStatusBanner({
  isConnected,
  isReconnecting = false,
}: ConnectionStatusBannerProps) {
  const { t } = useTranslation();
  const [bannerState, setBannerState] = useState<BannerState>('hidden');
  const wasConnectedRef = useRef(false);
  const restoredTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const connectingDelayRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (restoredTimerRef.current) {
      clearTimeout(restoredTimerRef.current);
      restoredTimerRef.current = null;
    }
    if (connectingDelayRef.current) {
      clearTimeout(connectingDelayRef.current);
      connectingDelayRef.current = null;
    }

    if (!isConnected || isReconnecting) {
      if (wasConnectedRef.current) {
        setBannerState('connecting');
      } else {
        connectingDelayRef.current = setTimeout(() => {
          if (!wasConnectedRef.current) {
            setBannerState('connecting');
          }
        }, 3000);
      }
    } else if (isConnected) {
      const isFirstConnect = wasConnectedRef.current === false && bannerState === 'hidden';
      wasConnectedRef.current = true;

      if (isFirstConnect) {
        setBannerState('hidden');
      } else {
        setBannerState('restored');
        restoredTimerRef.current = setTimeout(() => {
          setBannerState('hidden');
        }, 1800);
      }
    }

    return () => {
      if (restoredTimerRef.current) clearTimeout(restoredTimerRef.current);
      if (connectingDelayRef.current) clearTimeout(connectingDelayRef.current);
    };
  }, [isConnected, isReconnecting]);

  return (
    <AnimatePresence>
      {bannerState !== 'hidden' && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
          className="pointer-events-none absolute inset-x-0 top-1 z-50 flex justify-center"
        >
          {bannerState === 'connecting' ? (
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="flex items-center gap-1.5 rounded-full bg-neutral-800/80 px-3 py-1 backdrop-blur-sm"
            >
              <motion.div
                className="h-1.5 w-1.5 rounded-full bg-amber-400"
                animate={{ opacity: [1, 0.3, 1] }}
                transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
              />
              <span className="text-[11px] font-medium text-white/90">
                {t('messaging.connecting')}
              </span>
            </motion.div>
          ) : (
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="flex items-center gap-1.5 rounded-full bg-neutral-800/80 px-3 py-1 backdrop-blur-sm"
            >
              <motion.div
                className="h-1.5 w-1.5 rounded-full bg-emerald-400"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 500, damping: 15 }}
              />
              <span className="text-[11px] font-medium text-white/90">
                {t('messaging.connectionRestored')}
              </span>
            </motion.div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default ConnectionStatusBanner;
